import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import { PalmDto } from '../../../../core/dto/device/palm/palm.dto'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { DateTime } from '../../../../core/common/date-time'

export const PalmUnlockBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<PalmDto>()
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedPalms, setSelectedPalms] = useState<PalmDto[]>([])

  const handleMutation = async (
    palm: PalmDto,
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = {
        type: sanitizeFetchType('unlock'),
        resource: ResourceName.PALMS,
        payload: {
          palmId: palm.id,
        },
      }
    } else {
      query = {
        type: sanitizeFetchType('unlock'),
        resource: ResourceName.PALMS,
        payload: {
          palmId: palm.id,
          recurringJobName,
          cronExpression,
          scheduledAt,
        },
      }
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: palm.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: palm.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleUnlock = async (
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
  ) => {
    setBulkStateDialogOpen(true)
    setUnlockDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    let selectedPalmsResponse
    try {
      selectedPalmsResponse = (await mutate(
        {
          type: sanitizeFetchType(GET_MANY),
          resource: ResourceName.PALMS,
          payload: {
            ids: selectedIds,
          },
        },
        { returnPromise: true, onFailure: (msg) => notify(msg, 'error') },
      )) as GetManyResult<PalmDto>
    } catch (err) {
      setBulkStateDialogOpen(false)
      notify((err as Error)?.message, 'error')
      return
    }
    if (!selectedPalmsResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedPalmsResponse.data.forEach((palm: PalmDto) => {
      pendingRequests.push(
        handleMutation(palm, recurringJobName, cronExpression, scheduledAt),
      )
      selectedPalms.push(palm)
      bulkProgressStatus.push({
        id: palm.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedPalms([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.PALMS_LOCK_BUTTON) ||
        loading ||
        mutationLoading
      }
      onClick={() => setUnlockDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.palms.actions.unlock'
            : 'resources.palms.actions.unlock-recurring',
        )}
      </Typography>
      <ConfirmDialog
        open={unlockDialogOpen}
        confirm={handleUnlock}
        source="lock"
        cancel={() => setUnlockDialogOpen(false)}
        action="remove"
        resource={ResourceName.PALMS}
        asRecurringJob={asRecurringJob}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedPalms}
        method="lock"
        statuses={bulkProgressStatus}
        resource={ResourceName.PALMS}
      />
    </MenuItem>
  )
}
